import { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Autocomplete,
	Typography,
	Chip,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
	ProductionLine,
	ProductionRole,
	useProduction,
} from './ProductionContext';
import { updateActiveTask, Util } from '../../util/Util';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import RefreshButton from '../../components/RefreshButton';
import { MONEY_LIMITS, QTY_LIMITS, parseMoneyInput, parseQtyInput, qtyMin, qtyStepAttr } from '../../util/quantity';
import VirtualizedListbox from '../../components/VirtualizedListbox';

import { lineTotal } from "../../util/quantity";
interface Props {
	setValidateCheck: (value: boolean) => void;
	/** running finalize view emphasizes expected vs actual */
	mode?: 'plan' | 'finalize';
}

const ProductionComponents = ({ setValidateCheck, mode = 'plan' }: Props) => {
	const { activeTask, setPendingTasks, items, setItems, isLocked } = useProduction();
	const { api } = useAuth();
	const { queueReRun, t } = useUI();
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [unitOptions, setUnitOptions] = useState<Record<number, string[]>>({});

	const components = activeTask?.components || [];
	const readOnly = isLocked;
	const showExpectedActual = mode === 'finalize' || activeTask?.status === 'RUNNING';

	// Backend rejects a repeated (item, role) pair — surface it before saving.
	const duplicateKeys = useMemo(() => {
		const seen = new Set<string>();
		const dupes = new Set<string>();
		components.forEach((c) => {
			if (c.item < 1) return;
			const key = `${c.role}-${c.item}`;
			if (seen.has(key)) dupes.add(key);
			seen.add(key);
		});
		return dupes;
	}, [components]);

	const isDuplicate = (line: ProductionLine) =>
		line.item > 0 && duplicateKeys.has(`${line.role}-${line.item}`);

	const fetchItems = async () => {
		if ((activeTask?.depot || -1) < 1) return;
		setIsItemLoading(true);
		// Items only from the selected production depot
		const res = await api.get(`depots/${activeTask?.depot}/items`);
		if (res?.status === 200) {
			setItems(res.data.items || []);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchItems);
		}
		setIsItemLoading(false);
	};

	const fetchUnits = async (itemId: number) => {
		if (itemId < 1 || unitOptions[itemId]) return;
		const res = await api.get(`items/${itemId}/unit_history`);
		if (res?.status === 200) {
			const list = (res.data.units || []).map((u: any) => u.unit as string);
			setUnitOptions((prev) => ({ ...prev, [itemId]: list }));
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => fetchUnits(itemId));
		}
	};

	useEffect(() => {
		fetchItems();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask?.depot]);

	// Warm the unit list for lines loaded from a BOM or an existing run.
	useEffect(() => {
		components.forEach((c) => fetchUnits(c.item));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [components.map((c) => c.item).join(',')]);

	useEffect(() => {
		const hasInput = components.some(
			(c) => c.role === 'INPUT' && c.item > 0 && c.quantity > 0
		);
		const hasOutput = components.some(
			(c) => c.role === 'OUTPUT' && c.item > 0 && c.quantity > 0
		);
		const allValid = components.every(
			(c) =>
				c.item > 0 &&
				c.quantity > 0 &&
				c.planned_qty >= 0 &&
				c.item_desc?.trim()
		);
		setValidateCheck(
			components.length > 0 &&
				hasInput &&
				hasOutput &&
				allValid &&
				duplicateKeys.size === 0
		);
	}, [components, duplicateKeys, setValidateCheck]);

	const itemOptions = useMemo(
		() =>
			items.map((item: any) => ({
				id: item.id,
				label: item.ItemName || item.item_name,
				cost: item.CostPrice ?? item.cost_price ?? 0,
				stock: item.Stock ?? item.stock ?? 0,
				isFractional: item.IsFractional === true,
			})),
		[items]
	);

	const updateComponents = (next: ProductionLine[]) => {
		updateActiveTask(setPendingTasks, { components: next });
	};

	const addLine = (role: ProductionRole) => {
		if (readOnly) return;
		updateComponents([
			...components,
			{
				key: uuidv4(),
				item: -1,
				item_desc: '',
				role,
				planned_qty: 1,
				quantity: 1,
				amount: 0,
			},
		]);
	};

	const removeLine = (key: string) => {
		if (readOnly) return;
		updateComponents(components.filter((c) => c.key !== key));
	};

	const patchLine = (key: string, patch: Partial<ProductionLine>) => {
		if (readOnly) return;
		updateComponents(
			components.map((c) => (c.key === key ? { ...c, ...patch } : c))
		);
	};

	const stockFor = (itemId: number) =>
		items.find((i: any) => Number(i.id) === Number(itemId))?.Stock ??
		items.find((i: any) => Number(i.id) === Number(itemId))?.stock ??
		null;

	const varianceChip = (expected: number, actual: number) => {
		const diff = actual - expected;
		if (diff === 0) {
			return <Chip size="small" label="0" color="default" variant="outlined" />;
		}
		return (
			<Chip
				size="small"
				label={diff > 0 ? `+${diff}` : `${diff}`}
				color={diff > 0 ? 'warning' : 'error'}
				variant="outlined"
			/>
		);
	};

	const renderRoleRows = (role: ProductionRole) => {
		const rows = components.filter((c) => c.role === role);
		const colSpan = showExpectedActual ? 9 : 7;
		return (
			<>
				<TableRow>
					<TableCell colSpan={colSpan} sx={{ bgcolor: 'action.hover', fontWeight: 600 }}>
						{role === 'INPUT'
							? t('common:production.inputs')
							: t('common:production.outputs')}{' '}
						({rows.length})
					</TableCell>
				</TableRow>
				{rows.map((line) => {
					const selected =
						itemOptions.find((o) => o.id === line.item) ||
						(line.item > 0
							? {
									id: line.item,
									label: line.item_desc,
									cost: line.amount,
									stock: 0,
									isFractional: line.isFractional === true,
							  }
							: null);
					const stock = stockFor(line.item);
					return (
						<TableRow key={line.key}>
							<TableCell sx={{ minWidth: 200 }}>
								<Autocomplete
									size="small"
									options={itemOptions}
									ListboxComponent={VirtualizedListbox}
									loading={isItemLoading}
									disabled={readOnly || (activeTask?.depot || -1) < 1}
									value={selected}
									getOptionLabel={(o) => o.label || ''}
									isOptionEqualToValue={(a, b) => a.id === b.id}
									onChange={(_, v) => {
										if (!v) {
											patchLine(line.key, {
												item: -1,
												item_desc: '',
												amount: 0,
											});
											return;
										}
										fetchUnits(v.id);
										patchLine(line.key, {
											item: v.id,
											item_desc: v.label,
											amount: line.amount || v.cost || 0,
											unit: line.unit || unitOptions[v.id]?.[0] || '',
											isFractional: v.isFractional === true,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder={t('common:tables.item')}
											error={isDuplicate(line)}
											helperText={
												isDuplicate(line)
													? t('common:production.duplicateComponent')
													: undefined
											}
										/>
									)}
								/>
							</TableCell>
							<TableCell>
								{stock != null ? Util.formatNumber(stock) : '—'}
							</TableCell>
							{showExpectedActual && (
								<TableCell>
									<TextField
										size="small"
										type="number"
										value={line.planned_qty}
										disabled={readOnly || mode === 'finalize'}
										inputProps={{ min: 0, max: QTY_LIMITS.max, step: qtyStepAttr(!!line.isFractional) }}
										onChange={(e) => {
											const parsed = parseQtyInput(e.target.value, !!line.isFractional);
											if (parsed === null) return;
											const planned = Math.max(0, Math.min(QTY_LIMITS.max, parsed));
											const patch: Partial<ProductionLine> = {
												planned_qty: planned || qtyMin(!!line.isFractional),
											};
											if (mode === 'plan' && activeTask?.status !== 'RUNNING') {
												patch.quantity = planned || qtyMin(!!line.isFractional);
											}
											patchLine(line.key, patch);
										}}
										sx={{ width: 90 }}
										label={t('common:production.expected')}
									/>
								</TableCell>
							)}
							<TableCell>
								<TextField
									size="small"
									type="number"
									value={line.quantity}
									disabled={readOnly}
									inputProps={{ min: qtyMin(!!line.isFractional), max: QTY_LIMITS.max, step: qtyStepAttr(!!line.isFractional) }}
									onChange={(e) => {
										const parsed = parseQtyInput(e.target.value, !!line.isFractional);
										if (parsed === null) return;
										patchLine(line.key, {
											quantity: Math.max(qtyMin(!!line.isFractional), Math.min(QTY_LIMITS.max, parsed)),
										});
									}}
									sx={{ width: 90 }}
									label={
										showExpectedActual
											? t('common:production.actual')
											: undefined
									}
								/>
							</TableCell>
							{showExpectedActual && (
								<TableCell>
									{varianceChip(line.planned_qty, line.quantity)}
								</TableCell>
							)}
							<TableCell>
								<Autocomplete
									freeSolo
									size="small"
									options={unitOptions[line.item] || []}
									disabled={readOnly || line.item < 1}
									value={line.unit || ''}
									onInputChange={(_, value) =>
										patchLine(line.key, { unit: value })
									}
									onChange={(_, value) =>
										patchLine(line.key, {
											unit: typeof value === 'string' ? value : value || '',
										})
									}
									sx={{ width: 110 }}
									renderInput={(params) => (
										<TextField {...params} placeholder={t('common:production.unit')} />
									)}
								/>
							</TableCell>
							<TableCell>
								<TextField
									size="small"
									type="number"
									value={line.amount}
									disabled={readOnly}
									inputProps={{ min: 0, max: MONEY_LIMITS.max, step: 0.01 }}
									onChange={(e) => {
										const parsed = parseMoneyInput(e.target.value);
										if (parsed === null) return;
										patchLine(line.key, {
											amount: Math.max(0, Math.min(MONEY_LIMITS.max, parsed)),
										});
									}}
									sx={{ width: 100 }}
								/>
							</TableCell>
							<TableCell>
								{Util.formatNumber(lineTotal(line.quantity, line.amount))}
							</TableCell>
							<TableCell>
								{!readOnly && (
									<IconButton size="small" onClick={() => removeLine(line.key)}>
										<DeleteOutline fontSize="small" />
									</IconButton>
								)}
							</TableCell>
						</TableRow>
					);
				})}
			</>
		);
	};

	return (
		<Box sx={{ mt: 2 }}>
			<Box
				sx={{
					mb: 1,
					display: 'flex',
					gap: 1,
					alignItems: 'center',
					flexWrap: 'wrap',
				}}
			>
				<Typography variant="subtitle2" sx={{ flex: 1 }}>
					{t('common:production.components')}
					{(activeTask?.depot || -1) < 1 && (
						<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
							{t('common:production.selectDepotFirst')}
						</Typography>
					)}
				</Typography>
				<RefreshButton onClick={fetchItems} isWorking={isItemLoading} />
				{!readOnly && mode !== 'finalize' && (
					<>
						<Button
							size="small"
							startIcon={<Add />}
							onClick={() => addLine('INPUT')}
							disabled={(activeTask?.depot || -1) < 1}
						>
							{t('common:production.addInput')}
						</Button>
						<Button
							size="small"
							startIcon={<Add />}
							onClick={() => addLine('OUTPUT')}
							disabled={(activeTask?.depot || -1) < 1}
						>
							{t('common:production.addOutput')}
						</Button>
					</>
				)}
			</Box>

			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{t('common:tables.item')}</TableCell>
						<TableCell>{t('common:tables.stock')}</TableCell>
						{showExpectedActual && (
							<TableCell>{t('common:production.expected')}</TableCell>
						)}
						<TableCell>
							{showExpectedActual
								? t('common:production.actual')
								: t('common:tables.quantity')}
						</TableCell>
						{showExpectedActual && (
							<TableCell>{t('common:production.variance')}</TableCell>
						)}
						<TableCell>{t('common:production.unit')}</TableCell>
						<TableCell>{t('common:tables.unitCost')}</TableCell>
						<TableCell>{t('common:tables.amount')}</TableCell>
						<TableCell width={48} />
					</TableRow>
				</TableHead>
				<TableBody>
					{renderRoleRows('INPUT')}
					{renderRoleRows('OUTPUT')}
					{components.length === 0 && (
						<TableRow>
							<TableCell colSpan={showExpectedActual ? 9 : 7}>
								{t('common:production.emptyComponents')}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</Box>
	);
};

export default ProductionComponents;
